import type { Selector, State } from 'model/State';
import {
  MACContactSelector,
  MACCustomersByEmailData,
  MACPartnerDataSelector,
  PartnerEmailTemplateConfig,
} from '../models';
import { selectSwitchedUser } from './configUser';
import { EXPERT, RESELLER } from 'modules/auth/model/CurrentContact';
import { endUser } from 'modules/auth/selectors';

export const selectGetCustomerByEmail: Selector<MACCustomersByEmailData> = (state: S) =>
  state.mac.contact.getCustomerByEmail;

export const selectGetCustomerByEmailSuccess: MACContactSelector = (
  state: State,
  partnerEmail: string,
) =>
  state.mac.contact.getCustomerByEmail?.[partnerEmail]?.ifSuccess((r) => r) ||
  state.mac.contact.getCustomerByEmail?.[partnerEmail]?.error;

export const selectGetCustomerPartnerId = (state: State, email: string) =>
  state.mac.contact.getCustomerByEmail?.[email]?.ifSuccess((r) => r);

// TODO: Replace with an appropriate type
export const selectIsMemberOfGroup: Selector<unknown> = (state: State) =>
  state.mac.contact.isMemberOfGroup?.ifSuccess((r) => r);

export const selectIsEmail: Selector<unknown> = (state: State) =>
  state.mac.contact.isEmail?.ifSuccess((r) => r);

export const selectExternal: Selector<unknown> = (state: State) =>
  state.mac.contact.external?.ifSuccess((r) => r);

export const selectPartnerName: MACPartnerDataSelector = (
  state: State,
  partnerId: string | number,
) => state.mac.contact.partnerName?.[partnerId]?.ifSuccess((r) => r);

export const selectContactsForOrganisation = (state: State, partnerId: string | number) =>
  state.mac.contact.contactsForOrganisation?.[partnerId]?.ifSuccess((r) => r);

export const selectValidateCountryChange = (state: State, country: string) =>
  state.mac.contact.validateCountryChange?.[country]?.ifSuccess((r) => r);

export const selectEnableDisableMainContact: Selector<unknown> = (state: State) =>
  state.mac.contact.mainContactEnableDisable?.ifSuccess((r) => r);

export const selectDeleteContactSuccess: Selector<unknown> = (state: State) =>
  state.mac.contact.deleteContact?.ifSuccess((r) => r);

export const selectAddNewPartnerContactSuccess: Selector<unknown> = (state: State) =>
  state.mac.contact.addNewPartnerContact?.success;

export const selectAddNewPartnerContact: Selector<unknown> = (state: State) =>
  state.mac.contact.addNewPartnerContact;

export const getCurrentMACContactDetails = (state: State) => {
  const currentMACUserEmail = selectSwitchedUser(state);
  return selectGetCustomerByEmail(state)[currentMACUserEmail];
};

export const getCurrentMACContactName = (state: State) =>
  getCurrentMACContactDetails(state)?.ifSuccess(
    (currentContact) => currentContact?.contactDetails?.firstName || null,
  ) || null;

export const getCurrentMACContactEmail = (state: State) =>
  getCurrentMACContactDetails(state)?.ifSuccess(
    (currentContact) => currentContact?.contactDetails?.email || null,
  ) || null;

export const getCurrentMACContactOrgCountry = (state: State) =>
  getCurrentMACContactDetails(state)?.ifSuccess(
    (currentContact) => currentContact?.organisationDetails?.country || null,
  ) || null;

export const getCurrentMACContactOrganization = (state: State) =>
  getCurrentMACContactDetails(state)?.ifSuccess(
    (currentContact) => currentContact?.organisationDetails?.name || null,
  ) || null;

export const getMACPayOnAccount = (state: State) =>
  getCurrentMACContactDetails(state)?.ifSuccess(
    (currentContact) => currentContact?.organisationDetails?.payOnAccount || null,
  ) || null;

export const getMACUserChannel = (state: State) => {
  const currentMACContact = getCurrentMACContactDetails(state)?.ifSuccess((r) => r);

  if (currentMACContact?.organisationDetails) {
    const { organisationType } = currentMACContact.organisationDetails;
    const isExpert = organisationType === EXPERT;
    const isReseller = organisationType === RESELLER;
    const isPartner = isExpert || isReseller;
    return {
      organisationType,
      isExpert,
      isReseller,
      isPartner,
    };
  }
  return endUser;
};

export const selectPartnerEmailConfig = (
  state: State,
  partnerId: string | number,
): [PartnerEmailTemplateConfig] =>
  state.mac.contact.partnerEmailConfig?.[partnerId]?.ifSuccess((r) => r);

export const selectUpdatePartnerEmailConfig: Selector<unknown> = (state: State) =>
  state.mac.contact.updatePartnerEmailConfig;
